// --- src/pages/AdminDashboard.jsx ---
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Server, Users, Activity, Database, Package,
  CreditCard, XCircle, AlertCircle, CheckCircle,
  DollarSign, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AdminNav from '../components/AdminNav';
import HomeButton from '../components/HomeButton';
// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:    { color: 'bg-green-500/10 text-green-400 border-green-500/20',    label: 'Active' },
    pending:   { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Pending' },
    rejected:  { color: 'bg-red-500/10 text-red-400 border-red-500/20',          label: 'Rejected' },
    suspended: { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20',    label: 'Suspended' },
    paused:    { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Paused' },
    cancelled: { color: 'bg-red-500/10 text-red-400 border-red-500/20',          label: 'Cancelled' },
  };
  const s = map[status] || map.active;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.color}`}>
      {s.label}
    </span>
  );
};

// ─── Plan badge ───────────────────────────────────────────────────────────────
const PlanBadge = ({ slug }) => {
  const map = {
    'web-hosting':       'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'namespace':         'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'managed-app':       'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'dedicated-cluster': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${map[slug] || 'bg-slate-500/10 text-slate-400 border-slate-500/20'} capitalize`}>
      {slug?.replace(/-/g, ' ') || 'Unknown'}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('deployments');

  // — deployments state —
  const [stats, setStats] = useState({ totalDeployments: 0, activeServers: 0, totalUsers: 0, uptime: 0 });
  const [deployments, setDeployments]               = useState([]);
  const [groupedByNamespace, setGroupedByNamespace] = useState({});
  const [namespaces, setNamespaces]                 = useState([]);
  const [selectedNamespace, setSelectedNamespace]   = useState('all');

  // — subscriptions state —
  const [subscriptions, setSubscriptions]   = useState([]);
  const [plans, setPlans]                   = useState([]);
  const [cancellingId, setCancellingId]     = useState(null);
  const [showNewSubModal, setShowNewSubModal] = useState(false);
  const [subFormLoading, setSubFormLoading] = useState(false);
  const [subFormError, setSubFormError]     = useState('');

  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, deploymentsRes, subsRes, plansRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/deployments'),
        api.get('/admin/subscriptions'),
        api.get('/stripe/plans'),
      ]);
      setStats(statsRes.data);
      const deploymentsData = deploymentsRes.data.deployments || deploymentsRes.data;
      setDeployments(Array.isArray(deploymentsData) ? deploymentsData : []);
      setGroupedByNamespace(deploymentsRes.data.groupedByNamespace || {});
      setNamespaces(deploymentsRes.data.namespaces || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCancelSubscription = async (id, planName) => {
    if (!window.confirm(`Cancel the ${planName} subscription? This cannot be undone.`)) return;
    setCancellingId(id);
    try {
      await api.post(`/admin/subscriptions/${id}/cancel`);
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'cancelled' } : s));
    } catch (err) {
      console.error('Error cancelling subscription:', err);
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':     return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'degraded':    return 'bg-red-500/10 text-red-400 border border-red-500/20';
      case 'progressing': return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
      case 'missing':     return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      case 'unknown':     return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
      default:            return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
    }
  };

  const getSyncStatusColor = (syncStatus) => {
    switch (syncStatus?.toLowerCase()) {
      case 'synced':    return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'outofsync': return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      case 'unknown':   return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
      default:          return 'bg-gray-500/10 text-gray-400 border border-gray-500/20';
    }
  };

  const filteredDeployments = selectedNamespace === 'all'
    ? deployments
    : groupedByNamespace[selectedNamespace] || [];

  const activeSubCount = subscriptions.filter(s => s.status === 'active').length;
  const monthlySpend   = subscriptions
    .filter(s => s.status === 'active' && s.billing_cycle === 'monthly')
    .reduce((sum, s) => sum + parseFloat(s.price_total || 0), 0)
    .toFixed(2);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <AdminNav />
      <HomeButton />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Stats Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-2xl font-bold">{stats.totalDeployments}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Total Deployments</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-2xl font-bold">{stats.activeServers}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Active Servers</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <span className="text-2xl font-bold">{stats.totalUsers}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Total Users</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-2xl font-bold">{stats.uptime}%</span>
            </div>
            <h3 className="text-slate-400 text-sm">System Uptime</h3>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex flex-wrap gap-1 mb-6 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
          {[
            { key: 'deployments',   label: 'Deployments',   icon: Server,      count: deployments.length },
            { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard,  count: subscriptions.length },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span>{tab.label} ({tab.count})</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Tab: Deployments ── */}
        {activeTab === 'deployments' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Recent Deployments</h2>
              {namespaces.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  <select
                    value={selectedNamespace}
                    onChange={(e) => setSelectedNamespace(e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Namespaces ({deployments.length})</option>
                    {namespaces.map((ns) => (
                      <option key={ns} value={ns}>{ns} ({groupedByNamespace[ns]?.length || 0})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-slate-800">
                    <th className="pb-3 text-slate-400 font-medium">Application</th>
                    <th className="pb-3 text-slate-400 font-medium">Namespace</th>
                    <th className="pb-3 text-slate-400 font-medium">User</th>
                    <th className="pb-3 text-slate-400 font-medium">Health</th>
                    <th className="pb-3 text-slate-400 font-medium">Sync Status</th>
                    <th className="pb-3 text-slate-400 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeployments.length > 0 ? (
                    filteredDeployments.map((deployment) => (
                      <tr key={deployment.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="py-4 font-medium">{deployment.name}</td>
                        <td className="py-4">
                          <span className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs font-mono">{deployment.namespace}</span>
                        </td>
                        <td className="py-4 text-slate-400">{deployment.user}</td>
                        <td className="py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(deployment.status)}`}>{deployment.status}</span>
                        </td>
                        <td className="py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSyncStatusColor(deployment.syncStatus)}`}>{deployment.syncStatus}</span>
                        </td>
                        <td className="py-4 text-slate-400">{deployment.date}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        {selectedNamespace === 'all' ? 'No deployments yet' : `No deployments in ${selectedNamespace} namespace`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Namespace summary cards */}
            {namespaces.length > 1 && (
              <div className="mt-8">
                <h2 className="text-xl font-bold mb-4">Namespaces Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {namespaces.map((ns) => {
                    const apps = groupedByNamespace[ns] || [];
                    const healthyCount = apps.filter(app => app.status === 'Healthy').length;
                    const syncedCount  = apps.filter(app => app.syncStatus === 'Synced').length;
                    return (
                      <div
                        key={ns}
                        className="bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-slate-600 transition-colors cursor-pointer"
                        onClick={() => setSelectedNamespace(ns)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-lg">{ns}</h3>
                          <span className="text-2xl font-bold text-blue-400">{apps.length}</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-slate-400">
                            <span>Healthy:</span><span className="text-green-400">{healthyCount}/{apps.length}</span>
                          </div>
                          <div className="flex justify-between text-slate-400">
                            <span>Synced:</span><span className="text-green-400">{syncedCount}/{apps.length}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Subscriptions ── */}
        {activeTab === 'subscriptions' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <div className="flex items-center gap-6">
                <h3 className="font-semibold text-white">All Subscriptions</h3>
                <span className="text-sm text-slate-400">{activeSubCount} active · <span className="text-purple-400">${monthlySpend}/mo</span></span>
              </div>
            </div>

            {subscriptions.length === 0 ? (
              <div className="py-16 text-center">
                <CreditCard className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400 mb-1">No subscriptions yet</p>
                <p className="text-slate-500 text-sm">Subscriptions created by company admins will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-left">
                      <th className="px-6 py-3 text-slate-400 font-medium">Company</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Plan</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Status</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Billing</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Total</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Next Billing</th>
                      <th className="px-6 py-3 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map(sub => (
                      <tr key={sub.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="px-6 py-4 text-slate-300">{sub.company_name || '—'}</td>
                        <td className="px-6 py-4">
                          <div>
                            <PlanBadge slug={sub.plan_slug} />
                            {sub.notes && <p className="text-xs text-slate-500 mt-1">{sub.notes}</p>}
                          </div>
                        </td>
                        <td className="px-6 py-4"><StatusBadge status={sub.status} /></td>
                        <td className="px-6 py-4 text-slate-400 capitalize">{sub.billing_cycle}</td>
                        <td className="px-6 py-4 font-semibold text-white">${sub.price_total}</td>
                        <td className="px-6 py-4 text-slate-400">
                          {sub.next_billing_at
                            ? new Date(sub.next_billing_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {sub.status !== 'cancelled' ? (
                            <button
                              onClick={() => handleCancelSubscription(sub.id, sub.plan_name)}
                              disabled={cancellingId === sub.id}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {cancellingId === sub.id
                                ? <div className="w-3 h-3 border border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                : <XCircle className="w-3 h-3" />
                              }
                              Cancel
                            </button>
                          ) : (
                            <span className="text-xs text-slate-500">Cancelled</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminDashboard;
